import React, { useContext, useEffect, useState } from 'react';
import * as qs from 'query-string';
import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { Panel } from 'primereact/panel';
import { NotificationContext } from '../../components/GrowlNotifications';
import { Loading } from '../../components/Loading';
import { growlActionSuccess, growlErrors } from '../../utils/NotificationUtil';
import { NumberOfEmployees, SUCCESS, SubscriptionStatus } from '../../shared/Constants';
import {
  buildSubscriptionPeriodText,
  getFormattedDate,
  getNumberOfEmployeesLabel,
  getSubscriptionStatusText,
} from '../../utils/AppUtil';
import SubscriptionActionButtons from './SubscriptionActionButtons';
import Pill from '../../components/Pill';

const SUBSCRIPTION_INFORMATION_QUERY = gql`
  query subscriptionInformationQuery($accountId: ID!) {
    account(id: $accountId) {
      id
      displayName
      subscriptionInformation {
        currentSubscription {
          status
          purchasedOn
          endsOn
          status
          numberOfEmployees
          duration
          payedAmount
        }
      }
    }
  }
`;

const RENEW_SUBSCRIPTION_MUTATION = gql`
  mutation renewSubscriptionMutation($managingAccountId: ID!, $clientAccountId: ID!) {
    renewSubscription(managingAccountId: $managingAccountId, clientAccountId: $clientAccountId)
  }
`;

const i18nMessages = defineMessages({
  success: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
  manageNoLimit: {
    id: 'business-center.manage-no-limit',
    defaultMessage: 'Manage NoLimit',
  },
  month: {
    id: 'account.subscription.month',
    defaultMessage: 'month',
  },
  months: {
    id: 'account.subscription.months',
    defaultMessage: 'months',
  },
  employees: {
    id: 'account.subscription.employees',
    defaultMessage: 'employees',
  },
  subscription: {
    id: 'account.subscription',
    defaultMessage: 'Subscription:',
  },
  accountId: {
    id: 'account.id',
    defaultMessage: 'Account ID:',
  },
  name: {
    id: 'account.subscription.name',
    defaultMessage: 'Name:',
  },
  startDate: {
    id: 'account.subscription.start-date',
    defaultMessage: 'Start date:',
  },
  status: {
    id: 'account.subscription.status',
    defaultMessage: 'Status:',
  },
  expirationRenewal: {
    id: 'account.subscription.expiration-renewal',
    defaultMessage: 'Expiration/renewal:',
  },
  active: {
    id: 'account.subscription.active-status',
    defaultMessage: 'Active',
  },
  trialing: {
    id: 'account.subscription.trialing-status',
    defaultMessage: 'Trialing',
  },
  paused: {
    id: 'account.subscription.paused-status',
    defaultMessage: 'Paused',
  },
});

const DEFAULT_ACCOUNT_STATE = {
  id: null,
  displayName: null,
  subscriptionInformation: {
    currentSubscription: {
      purchasedOn: null,
      endsOn: null,
      status: SubscriptionStatus.ACTIVE,
      numberOfEmployees: NumberOfEmployees._0_9,
      duration: 1,
      payedAmount: 0,
    },
  },
};

const ManageOwnSubscription = () => {
  const [account, setAccount] = useState(DEFAULT_ACCOUNT_STATE);

  const { aid: accountId } = useParams();
  const { search } = useLocation();
  const { creditCardChange } = qs.parse(search);
  const { formatMessage, formatDate } = useIntl();
  const growl = useContext(NotificationContext);
  const history = useHistory();

  const [renewSubscriptionMutation] = useMutation(RENEW_SUBSCRIPTION_MUTATION);
  const { loading, error, data } = useQuery(SUBSCRIPTION_INFORMATION_QUERY, {
    variables: {
      accountId,
    },
    fetchPolicy: 'no-cache',
  });

  const { account: requestAccount } = data;

  useEffect(() => {
    if (requestAccount) {
      setAccount(requestAccount);
    }
  }, [requestAccount]);

  useEffect(() => {
    if (creditCardChange === SUCCESS) {
      growlActionSuccess(growl, formatMessage(i18nMessages.success));
    }
    // eslint-disable-next-line
  }, [creditCardChange]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    growlErrors(growl, error);
  }

  const {
    id,
    displayName,
    subscriptionInformation: {
      currentSubscription: { purchasedOn, endsOn, status, numberOfEmployees, duration, payedAmount },
    },
  } = account;

  const statusText = getSubscriptionStatusText(
    status,
    formatMessage(i18nMessages.active),
    formatMessage(i18nMessages.trialing),
    formatMessage(i18nMessages.paused),
  );

  const numberOfEmployeesIncluded = getNumberOfEmployeesLabel(numberOfEmployees);

  const buildNumberOfEmployeesText = () => `${numberOfEmployeesIncluded} ${formatMessage(i18nMessages.employees)}`;

  const subscriptionPeriodText = buildSubscriptionPeriodText(
    formatMessage(i18nMessages.months),
    formatMessage(i18nMessages.month),
    duration,
  );
  const numberOfEmployeesText = buildNumberOfEmployeesText();

  const subscriptionText = `${numberOfEmployeesText}, $${payedAmount.toLocaleString()}, ${subscriptionPeriodText}`;

  const renewSubscription = async () => {
    try {
      await renewSubscriptionMutation({
        variables: {
          managingAccountId: accountId,
          clientAccountId: accountId,
        },
      });

      growlActionSuccess(growl);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    }
  };

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <div className="p-sm-12 p-md-6">
            <Panel header={formatMessage(i18nMessages.manageNoLimit)}>
              <div>
                <div className="existing-subscription-info">
                  <div className="font-weight-600">{formatMessage(i18nMessages.subscription)}</div>
                  <div>{subscriptionText}</div>
                </div>
                <div className="existing-subscription-info">
                  <div className="font-weight-600">{formatMessage(i18nMessages.accountId)}</div>
                  <div>{id}</div>
                </div>
                <div className="existing-subscription-info">
                  <div className="font-weight-600">{formatMessage(i18nMessages.name)}</div>
                  <div>{displayName}</div>
                </div>
                <br />
                <div className="existing-subscription-info">
                  <div>
                    <strong>{formatMessage(i18nMessages.status)}</strong>
                  </div>
                  <Pill type={status}>{statusText}</Pill>
                </div>
                <br />
                <div className="existing-subscription-info">
                  <div className="font-weight-600">{formatMessage(i18nMessages.startDate)}</div>
                  <div>{getFormattedDate(purchasedOn, formatDate)}</div>
                </div>
                <div className="existing-subscription-info">
                  <div className="font-weight-600">{formatMessage(i18nMessages.expirationRenewal)}</div>
                  <div>{getFormattedDate(endsOn, formatDate)}</div>
                </div>
              </div>
              <br />
              <SubscriptionActionButtons
                redirectToChangeSubscriptionPage={() => {
                  history.push(`/accounts/${accountId}/change-own-subscription?showStoreCreditExplanation=true`);
                }}
                redirectToStopSubscriptionPage={() => {
                  history.push(`/accounts/${accountId}/stop-own-subscription`);
                }}
                managingAccountId={accountId}
                clientAccountId={accountId}
                status={status}
                renewSubscription={renewSubscription}
                redirectToBuySubscriptionPage={() => {
                  history.push(`/buy-own-subscription?accountId=${accountId}`);
                }}
              />
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageOwnSubscription;
